import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { Droppable, FONT_WEIGHT_BOLD, InfoTooltip, showDndDebugInfo } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { CONDITION_TYPE, NEGATION_CONDITION_NAME, TYPE_DROPPABLE } from "../../constants";
import type { DroppableData } from "../../dnd/dndSchemas";
import { ChildConditionEditor } from "../../editors/ChildConditionEditor";
import { DROPZONE_PADDING } from "../../editors/constants";
import {
  ConditionPlaceholderDropzone,
  ConditionsListContainer,
  ResponseZoneContainer,
  ResponseZoneHeader,
} from "../../editors/shared";
import { makeConditionDroppableId } from "../../editors/utils";
import { useResponseEditorContext } from "../../response-editor";
import { useResponseDesigner } from "../../ResponseByIdContext";
import type { negationConditionBuilder } from "./negationConditionBuilder";

const HeaderTitle = styled.div`
  font-weight: ${FONT_WEIGHT_BOLD};
  color: ${({ theme }) => theme.secondary};
  margin-right: 4px;
`;

const StyledInfoTooltip = styled(InfoTooltip)`
  color: ${({ theme }) => theme.secondary};
`;

const NegationConditionsListContainer = styled(ConditionsListContainer)`
  padding-bottom: ${DROPZONE_PADDING}px;
`;

export const NegationConditionEditor: typeof negationConditionBuilder.views.edit = ({ id, children: { values } }) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { removeCondition } = useResponseDesigner();
  const { disabledDropzoneIds } = useResponseEditorContext();

  const dropzoneDisabled = !!values.length;
  const isDisabled = dropzoneDisabled || disabledDropzoneIds.includes(id);

  return (
    <>
      {showDndDebugInfo && (
        <span
          css={css`
            position: absolute;
            background: rgba(0, 0, 0, 0.25);
            color: #fff;
          `}
        >
          Negation: {id}
        </span>
      )}
      <ResponseZoneContainer>
        <ResponseZoneHeader>
          <HeaderTitle>
            {formatMessage("negate")} <StyledInfoTooltip tooltip={formatMessage("tooltip")} />
          </HeaderTitle>
        </ResponseZoneHeader>
        <Droppable
          id={makeConditionDroppableId(id)}
          data={
            {
              rule: { id, type: TYPE_DROPPABLE, ruleType: CONDITION_TYPE, typeId: NEGATION_CONDITION_NAME },
            } satisfies DroppableData
          }
          hideBorder={!dropzoneDisabled}
          disabled={isDisabled}
          accepts={{ "rule.ruleType": [CONDITION_TYPE] }}
          color="secondary"
        >
          {dropzoneDisabled ? (
            <>
              <NegationConditionsListContainer>
                <ChildConditionEditor
                  condition={values[0]}
                  index={0}
                  group={`${id}`}
                  dropzoneDisabled={dropzoneDisabled}
                  onRemoveCondition={removeCondition}
                />
              </NegationConditionsListContainer>
            </>
          ) : (
            <ConditionPlaceholderDropzone>{formatMessage("dragConditionToNegate")}</ConditionPlaceholderDropzone>
          )}
        </Droppable>
      </ResponseZoneContainer>
    </>
  );
};

const formatStrings = {
  negate: {
    en: "Flip Result",
  },
  dragConditionToNegate: {
    en: "Drag condition here.",
  },
  tooltip: {
    en: "Evaluates the condition within, then returns the opposite result, i.e. true becomes false, false becomes true.",
  },
};
